import React, {
  useEffect,
  useLayoutEffect,
  useState,
  useRef,
  useContext,
} from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import patternFill from "highcharts/modules/pattern-fill";
import WebsocketWrapperContext from "../context/WebsocketWrapperContext";
import { blackScholes } from "black-scholes";
import { useTheme } from "../context/ThemeContext";
import { Tooltip as ReactTooltip } from "react-tooltip";
import QuestionMarks from "../images/questionmark.svg";
import QuestionMark from "../images/QuestionMark";

patternFill(Highcharts);

const StrategyGraph = ({
  data,
  selectedData,
  ltp,
  leastExpiry,
  allOptionData,
  selectedSymbol, // Add selectedSymbol prop
  currentOptionsData, // Add currentOptionsData prop
  selectedExpiry // Add selectedExpiry prop
}) => {
  const {
    updateSymbolList,
    websocketConnection,
    checkWebsocketConnection,
    subscribeToSymbols,
    unsubscribeToSymbols,
    symbolList,
    symbolData,
  } = useContext(WebsocketWrapperContext);
  var iv = require("implied-volatility");
  /*theme*/
  const { theme, toggleTheme } = useTheme();
  const [calculatedIV, setCalculatedIV] = useState(0);
  const x_axis = data?.["x_axis"];
  const y_axis = data?.["y_axis"];
  const [ltpLine, setLtpLine] = useState(data?.ltpIndex);
  const [lowerBand1Val, setLowerBand1Val] = useState(0);
  const [lowerBand2Val, setLowerBand2Val] = useState(0);
  const [upperBand1Val, setUpperBand1Val] = useState(0);
  const [upperBand2Val, setUpperBand2Val] = useState(0);
  const [targetPnLLine, setTargetPnLLine] = useState(data?.["y_axis"]);
  const [activeButton, setActiveButton] = useState("payoffchart");
  const chartComponent = useRef(null);

  // Cleanup effect when component unmounts or key props change
  useEffect(() => {
    return () => {
      // Cleanup when component unmounts
      if (chartComponent.current) {
        const chart = chartComponent.current;
        if (chart.customPlotLine) {
          chart.customPlotLine.destroy();
        }
        if (chart.xAxis && chart.xAxis[0]) {
          chart.xAxis[0].removePlotLine('current-price-line');
        }
      }
    };
  }, [selectedSymbol, selectedExpiry]); // Cleanup when symbol or expiry changes

  // Add OI legend component
  const OILegend = () => {
    if (!symbolData || !selectedSymbol || !currentOptionsData || !selectedExpiry) {
      return null;
    }

    const options = currentOptionsData[selectedExpiry]?.options;
    if (!options || !ltp) return null;

    // Find the closest strike to current LTP
    let closestStrike = options[0]?.strike;
    let minDiff = Math.abs(ltp - closestStrike);

    options.forEach((option) => {
      const strike = parseFloat(option.call?.strike || option.put?.strike);
      const diff = Math.abs(ltp - strike);
      if (diff < minDiff) {
        minDiff = diff;
        closestStrike = strike;
      }
    });

    // Get OI data for the closest strike
    const closestOption = options.find(option =>
      parseFloat(option.call?.strike || option.put?.strike) === closestStrike
    );

    if (!closestOption) return null;

    const callOI = symbolData[closestOption.call?.code]?.oi || 0;
    const putOI = symbolData[closestOption.put?.code]?.oi || 0;

    const formatVolume = (volume) => {
      if (volume >= 10000000) {
        return `${(volume / 10000000).toFixed(2)}Cr`;
      } else if (volume >= 100000) {
        return `${(volume / 100000).toFixed(2)}L`;
      } else if (volume >= 1000) {
        return `${(volume / 1000).toFixed(2)}K`;
      } else {
        if (isNaN(volume)) {
          return "0";
        }
        return `${volume}`;
      }
    };

    return (
      <div className="flex items-center gap-4 text-xs">
        <div className="text-n400">
          OI data at <span className="font-bold">{closestStrike}</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-2 h-2 bg-pink-300 rounded-sm"></div>
          <span className="text-n400">Call OI:</span>
          <span className="font-bold">{formatVolume(callOI)}</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-2 h-2 bg-green-300 rounded-sm"></div>
          <span className="text-n400">Put OI:</span>
          <span className="font-bold">{formatVolume(putOI)}</span>
        </div>
      </div>
    );
  };

  function setAtmIndex() {
    let index = 0;
    try {
      if (allOptionData) {
        let optionData = allOptionData[Object.keys(allOptionData)[0]];
        if (optionData && optionData.options) {
          let curr = optionData.options[0].strike,
            diff = Math.abs(ltp - curr);
          for (let val = 0; val < optionData.options.length; val++) {
            let newdiff = Math.abs(ltp - optionData.options[val].strike);
            if (newdiff < diff) {
              diff = newdiff;
              curr = optionData.options[val];
              index = val;
            }
          }
          let atmIndex = optionData?.options[index]?.call;
          const targetDateTimeString = atmIndex.expiry_date + " 15:29:59"; // Target date and time
          const targetDateTime = new Date(targetDateTimeString);
          const currentDate = new Date();
          let timeToExpiration = Math.max(
            0,
            (targetDateTime - currentDate) / (24 * 60 * 60 * 365 * 1000) ||
            0.0002 / 365
          );
          let computedIv = (
            computeIV(
              symbolData?.[atmIndex.code]?.ltp,
              ltp,
              atmIndex?.strike,
              timeToExpiration,
              "call"
            ) * 100
          ).toFixed(2);
          setCalculatedIV(computedIv);
        }
      }
    } catch (error) {
      // console.log(error);
      // // console.log("Waiting for IV");
    }
  }

  useEffect(() => {
    if (data?.ltpIndex !== undefined) {
      setLtpLine(data?.ltpIndex);
    }
  }, [data]);

  function computeIV(optionPrice, spot, strike, timeToExpiration, optionType) {
    const riskFreeRate = 0.064515; //moved this out of the loop
    const initialGuessIv = iv.getImpliedVolatility(
      optionPrice,
      spot,
      strike,
      timeToExpiration,
      riskFreeRate,
      optionType
    );
    return initialGuessIv;
  }

  function computeTargetLine(
    spot,
    row,
    timeToExpiration,
    annualizedDaysCount,
    optionType
  ) {
    const riskFreeRate = 0.064515;
    let targetPrices = [];
    let rowIv = computeIV(
      parseFloat(row?.price),
      parseFloat(spot),
      row?.strike,
      annualizedDaysCount,
      optionType
    ).toFixed(2);
    if (parseInt(rowIv) > 1) {
      rowIv = rowIv / 100;
    }
    for (let val = 0; val < x_axis?.length; val++) {
      const optionPrice = blackScholes(
        x_axis[val],
        row?.strike,
        timeToExpiration,
        rowIv,
        riskFreeRate,
        optionType
      );
      targetPrices.push(optionPrice);
    }
    return targetPrices;
  }

  function updateTargetLine(g, daysCount) {
    let xaxis_length = x_axis?.length;
    let targetLine = Array.from({ length: xaxis_length }, () => 0);
    for (let val = 0; val < selectedData.length; val++) {
      let optionType = selectedData[val]?.isCALL ? "call" : "put";
      let ddate;
      if (selectedData[val].hasOwnProperty("expiry")) {
        ddate = new Date(selectedData[val].expiry);
      } else {
        ddate = new Date(selectedData[val].expiry_date);
      }
      let date = new Date();
      date.setDate(date.getDate() + daysCount);
      let expiry_days = Math.round((ddate - date) / (1000 * 60 * 60 * 24));
      expiry_days = expiry_days < 0 ? 0 : expiry_days;
      let timeToExpiration = Math.max(
        0,
        Math.abs(expiry_days + g) / 365 || 0.0002 / 365
      );
      let annualizedDaysCount = Math.max(
        0,
        (daysCount + expiry_days) / 365 || 0.0002 / 365
      );

      let line = computeTargetLine(
        ltp,
        selectedData[val],
        timeToExpiration,
        annualizedDaysCount,
        optionType
      );
      let updatedLine;
      let optionPrice = parseFloat(selectedData[val]?.price);
      let total_quantity =
        selectedData[val]?.lotSize * selectedData[val]?.lot_multiplier;
      if (selectedData[val]?.isBuy) {
        updatedLine = line.map((item) => (item - optionPrice) * total_quantity);
      } else {
        updatedLine = line.map((item) => (optionPrice - item) * total_quantity);
      }
      targetLine.map(
        (value, index) => (targetLine[index] += updatedLine[index])
      );

      let currentPriceIndex = 0;
      let minDiff = Math.abs(ltp - x_axis?.[0]);
      for (let i = 1; i < x_axis?.length; i++) {
        let diff = Math.abs(ltp - x_axis[i]);
        if (diff < minDiff) {
          minDiff = diff;
          currentPriceIndex = i;
        }
      }
      if (currentPriceIndex == -1) {
        currentPriceIndex = parseInt(x_axis.length / 2);
      }
      selectedData[val]["targetPrice"] = parseFloat(
        line[currentPriceIndex]
      ).toFixed(2);
    }
    const hasNaN = targetLine.some((value) => isNaN(value));
    if (!hasNaN) {
      setTargetPnLLine(targetLine);
    } else {
      setTargetPnLLine(data?.["y_axis"]);
    }
  }

  const customPattern = (color, borderColor) => {
    return {
      pattern: {
        path: {
          d: "M 0 0 L 10 0 ", // Inner path
          stroke: color,
          strokeWidth: 2,
        },
        width: 10,
        height: 10,
        patternTransform: "rotate(-50)",
      },
    };
  };

  function formatNumberWithCommasAndFontWeight(value) {
    const formattedValue = parseFloat(value).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

    const parts = formattedValue.split(".");
    const integerPart = parts[0];
    const decimalPart = parts[1];

    return (
      <span>
        {integerPart}.<span style={{ fontWeight: 400 }}>{decimalPart}</span>
      </span>
    );
  }

  const [lotsValue, setLotsValue] = useState(1);

  const [linechart, setlineChart] = useState({
    xAxis: {
      tickInterval: 100,
      categories: x_axis,
      title: false,
      labels: {
        enabled: true,
        rotation: 0,
        style: {
          textOverflow: "none",
        },
        formatter: function () {
          let chartWidth = this.chart.plotWidth;
          let labelCount = this.axis.tickPositions.length;
          let labelWidth = chartWidth / labelCount;
          let showLabels = labelCount;

          // Adjust the number of labels to show based on label width
          if (labelWidth < 100) {
            // Adjust the threshold as needed
            showLabels = Math.ceil(chartWidth / 100); // Show 1 label per 50 pixels
          }

          // Show only every nth label to fit the available space
          let index = this.axis.tickPositions.indexOf(this.pos);
          if (index % Math.ceil(labelCount / showLabels) !== 0) {
            return ""; // Return an empty string to skip label
          }

          return this.value;
        },
      },
    },
    yAxis: {
      title: {
        enabled: false,
      },
    },
    plotOptions: {
      series: {
        stacking: "normal",
        fillOpacity: 0.4,
      },
    },
    series: [
      {
        name: "",
        color: "#D8FDE2",
        negativeColor: "#FFD9D7",
        showInLegend: false,
      },
    ],
    chart: {
      type: "area",
      height: 346,
      style: {
        fontFamily: "Satoshi-Variable",
      },
      backgroundColor: "transparent",
    },
    title: {
      text: "",
    },
  });

  const calculatePlotBandWidth = (
    days_to_expire,
    ltp,
    calculatedIV,
    x_axis
  ) => {
    if (
      calculatedIV === undefined ||
      calculatedIV === null ||
      calculatedIV === 0
    ) {
      return {
        lowerBand1: null,
      };
    }

    const width = parseFloat(
      ltp * (calculatedIV / 100) * Math.sqrt(days_to_expire / 365)
    );

    const lowerBand1 = parseFloat(ltp) - width;
    const upperBand1 = parseFloat(ltp) + width;
    const lowerBand2 = parseFloat(ltp) - width * 2;
    const upperBand2 = parseFloat(ltp) + width * 2;

    const lowerBand1Index = closestIndex(lowerBand1, x_axis);
    const upperBand1Index = closestIndex(upperBand1, x_axis);
    const lowerBand2Index = closestIndex(lowerBand2, x_axis);
    const upperBand2Index = closestIndex(upperBand2, x_axis);

    const lowerBand1Value = x_axis[lowerBand1Index];
    const upperBand1Value = x_axis[upperBand1Index];
    const lowerBand2Value = x_axis[lowerBand2Index];
    const upperBand2Value = x_axis[upperBand2Index];
    setLowerBand1Val(lowerBand1Value);
    setLowerBand2Val(lowerBand2Value);
    setUpperBand1Val(upperBand1Value);
    setUpperBand2Val(upperBand2Value);

    return {
      lowerBand1,
      upperBand1,
      lowerBand2,
      upperBand2,
      lowerBand1Index,
      upperBand1Index,
      lowerBand2Index,
      upperBand2Index,
    };
  };

  const closestIndex = (band, x_axis) => {
    const index = x_axis?.reduce(
      (prevIndex, currentValue, currentIndex, arr) => {
        return Math.abs(currentValue - band) < Math.abs(arr[prevIndex] - band)
          ? currentIndex
          : prevIndex;
      },
      0
    );
    return index;
  };

  const [chartOptions, setChartOptions] = useState(linechart);

  const updateChart = () => {
    const {
      lowerBand1,
      upperBand1,
      lowerBand2,
      upperBand2,
      lowerBand1Index,
      upperBand1Index,
      lowerBand2Index,
      upperBand2Index,
    } = calculatePlotBandWidth(lotsValue, ltp, calculatedIV, x_axis);

    if (!x_axis?.length || !y_axis?.length) {
      return; // Don't update chart with invalid data
    }

    if (((upperBand1 == upperBand2) == lowerBand1) == lowerBand2) {
      return;
    }

    setChartOptions((prevOptions) => ({
      ...prevOptions,
      chart: {
        marginTop: 20,
        events: {
          load() {
            const chart = this;
            
            // Clear any existing custom plot lines
            if (chart.customPlotLine) {
              chart.customPlotLine.destroy();
            }
            
            // Clear any existing plotLines from xAxis
            if (chart.xAxis && chart.xAxis[0]) {
              chart.xAxis[0].removePlotLine('current-price-line');
            }
            
            const plotLineX = chart.xAxis[0].toPixels(data?.ltpIndex, true);
            const plotLineY1 = chart.yAxis[0].toPixels(
              chart.yAxis[0].min,
              true
            );
            const plotLineY2 = plotLineY1; // 80 pixels height //removed -80 from here

            // Draw the custom plot line and store reference
            chart.customPlotLine = chart.renderer
              .path(["M", plotLineX, plotLineY1, "L", plotLineX, plotLineY2])
              .attr({
                "stroke-width": 2,
                stroke: "#0F0F0F",
                zIndex: 2,
              })
              .add();
          },
          destroy() {
            // Clean up custom plot line when chart is destroyed
            if (this.customPlotLine) {
              this.customPlotLine.destroy();
            }
          },
        },
      },
      series: [
        {
          type: "area",
          data: y_axis,
          name: "Profit/Loss",
          color: "#12B16E",
          opacity: 0.7,
          negativeColor: "#E94C69",
          lineWidth: 2,
          showInLegend: false,
          marker: {
            enabled: false,
          },
        },
        {
          type: "line",
          data: targetPnLLine,
          name: "Target P&L",
          color: "#0075FF",
          opacity: 1,
          negativeColor: "#0075FF",
          lineWidth: 2,
          showInLegend: false,
          marker: {
            enabled: false,
          },
        },
      ],
      yAxis: [
        {
          gridLineColor: "#EFEFEF",
          tickInterval: 100,
          gridLineWidth: 0,
          minorGridLineWidth: 0,
          labels: {
            formatter: function () {
              let chartWidth = this.chart.plotHeight;
              let labelCount = this.axis.tickPositions.length;
              let labelWidth = chartWidth / labelCount;
              let showLabels = labelCount;

              // Adjust the number of labels to show based on label width
              if (labelWidth < 50) {
                // Adjust the threshold as needed
                showLabels = Math.ceil(chartWidth / 50); // Show 1 label per 50 pixels
              }
              // Show only every nth label to fit the available space
              let index = this.axis.tickPositions.indexOf(this.pos);
              if (index % Math.ceil(labelCount / showLabels) !== 0) {
                return ""; // Return an empty string to skip label
              }

              return this.value.toLocaleString();
            },
            style: {
              color: theme === "light" ? "#575757" : "#FFF",
              fontSize: "10px",
              fontWeight: "500",
              fontFamily: "Graphik, sans-serif",
            },
            x: -10, // Adding horizontal padding for y-axis labels
            y: 0,
          },

          plotLines: [
            {
              value: 0,
              width: 2,
              color: "#efefef",
            },
          ],
          title: {
            enabled: true,
            text: "Profit / Loss",
            style: {
              color: theme === "light" ? "#B7B7B7" : "#FFF",
              fontSize: "10px",
              fontWeight: "500",
            },
            x: -5,
            align: "middle",
          },
          opposite: false,
        },
        {
          labels: {
            style: {
              color: theme === "light" ? "#575757" : "#FFF",
              fontSize: "12px",
              fontWeight: "500",
            },
            formatter: function () {
              // Format OI percentage labels
              return Math.abs(this.value).toFixed(0) + "%";
            }
          },
          title: {
            text: "OI %",
            style: {
              color: theme === "light" ? "#B7B7B7" : "#FFF",
              fontSize: "10px",
              fontWeight: "500",
            }
          },
          opposite: true, // This places the secondary y-axis on the right side
          min: -100,
          max: 100,
          tickInterval: 20
        },
      ],
      xAxis: {
        lineColor: "transparent",
        gridLineColor: "#EFEFEF",
        tickInterval: 16,
        allowDecimals: false,
        categories: x_axis.map((value) => value.toLocaleString("en-IN")),
        labels: {
          style: {
            color: theme === "light" ? "#6F6F6F" : "#FFF",
            fontSize: "10px",
            fontWeight: "500",
            fontFamily: "Graphik, sans-serif",
          },
          y: 20,
          x: 0,
        },
        plotLines: [
          {
            color: theme == "dark" ? "#ffffff" : "#0F0F0F",
            width: 2,
            value: ltpLine,
            zIndex: 2,
            id: 'current-price-line', // Add unique ID
            label: {
              style: {
                fontWeight: "bold",
                overflow: "visible",
              },
              text: "",
              textAlign: "center",
              rotation: 0,
              zIndex: 2,
              y: 0,
            },
          },
          {
            color: "#EBEBEB",
            width: 2,
            value: lowerBand2Index,
            zIndex: 2,
            label: {
              style: {
                fontWeight: "bold",
                overflow: "visible",
              },
              text: "",
              textAlign: "center",
              rotation: 0,
              zIndex: 2,
              y: 0,
            },
          },
          {
            color: "#EBEBEB",
            width: 2,
            value: lowerBand1Index,
            zIndex: 2,
            label: {
              style: {
                fontWeight: "bold",
                overflow: "visible",
              },
              text: "",
              textAlign: "center",
              rotation: 0,
              zIndex: 2,
              y: 0,
            },
          },
          {
            color: "#EBEBEB",
            width: 2,
            value: upperBand1Index,
            zIndex: 2,
            label: {
              style: {
                fontWeight: "bold",
                overflow: "visible",
              },
              text: "",
              textAlign: "center",
              rotation: 0,
              zIndex: 2,
              y: 0,
            },
          },
          {
            color: "#EBEBEB",
            width: 2,
            value: upperBand2Index,
            zIndex: 2,
            label: {
              style: {
                fontWeight: "bold",
                overflow: "visible",
              },
              text: "",
              textAlign: "center",
              rotation: 0,
              zIndex: 2,
              y: 0,
            },
          },
        ],
        plotBands: [
          {
            color: customPattern("transparent"),
            from: lowerBand2Index,
            to: upperBand2Index,
            zIndex: 1,
            label: {
              text: "-2SD",
              align: "left",
              x: -18,
              y: -6,
              style: {
                color: theme === "light" ? "#6F6F6F" : "#FFF",
                fontSize: "10px",
                fontWeight: "500",
                fontFamily: "Graphik, sans-serif",
              },
            },
          },
          {
            color: customPattern("#B7B7B7"),
            from: lowerBand1Index,
            to: upperBand1Index,
            zIndex: 1,
            position: "absolute",

            label: {
              text: "-1SD",
              align: "left",
              x: -10,
              y: -6,
              style: {
                color: theme === "light" ? "#6F6F6F" : "#FFF",
                fontSize: "10px",
                fontWeight: "500",
                fontFamily: "Graphik, sans-serif",
              },
            },
          },
          {
            color: customPattern("transparent"),
            from: lowerBand2Index,
            to: upperBand2Index,
            zIndex: 1,
            label: {
              text: "+2SD",
              align: "right",
              x: 15,
              y: -6,
              style: {
                color: theme === "light" ? "#6F6F6F" : "#FFF",
                fontSize: "10px",
                fontWeight: "500",
                fontFamily: "Graphik, sans-serif",
                overflow: "none",
              },
            },
          },
          {
            color: customPattern("#CFCFCF"),
            from: lowerBand1Index,
            to: upperBand1Index,
            zIndex: 1,
            label: {
              text: "+1SD",
              align: "right",
              x: 5,
              y: -6,
              style: {
                color: theme === "light" ? "#6F6F6F" : "#FFF",
                fontSize: "10px",
                fontWeight: "500",
                fontFamily: "Graphik, sans-serif",
              },
            },
          },
        ],
      },
      credits: {
        enabled: false,
      },
    }));
  };
  const handleButtonClick = (button) => {
    setActiveButton(button);
  };

  // for graph table:
  //for range slider
  const [sliderValue, setSliderValue] = useState(0);
  const [lowerLimit, setlower] = useState(0);
  const [duplicateVal, setdup] = useState(0);
  const [upperLimit, setupper] = useState(100);
  const [left, setleft] = useState(0);
  const [diff, setdiff] = useState(0);
  const [symb, setsymbol] = useState(null);
  const [expiries, setExpiries] = useState([]);
  const [selectedDate, setDate] = useState(new Date());
  const [chartError, setChartError] = useState(null);
  const [ltpIndex, setLtpIndex] = useState(0);

  const [daysleft, setdaysleft] = useState(0);
  const [daysCount, setdaysCount] = useState(0);
  const [daysToExpiry, setDaysToExpiry] = useState(0);

  useLayoutEffect(() => {
    let dt = new Date(leastExpiry);
    let varb = 0;
    let leastdate = new Date(leastExpiry + " 15:29:59");
    let g;
    selectedData.map((o) => {
      if (o == leastExpiry) {
        varb = true;
      }
      let ddate;
      if (o.hasOwnProperty("expiry")) {
        ddate = new Date(o.expiry);
      } else {
        if (o.net_quantity != 0) {
          ddate = new Date(o.expiry_date);
        } else {
          ddate = new Date(leastExpiry);
        }
      }
      leastdate = new Date(Math.min(ddate, leastdate));
    });

    if (!varb) {
      dt = new Date(leastdate);
      setDate(dt);
      let exd = new Date(leastdate);
      g = Math.round((dt - exd) / (1000 * 60 * 60 * 24));
      setdaysleft(g);
    }

    let td = new Date();
    let dates = [];
    let date = new Date();
    while (date <= dt) {
      dates.push(new Date(date));
      date.setDate(date.getDate() + 1);
    }
    // dates.push(new Date(date));
    setExpiries(dates);

    let exd = new Date();
    let daysCount = Math.round((date - exd) / (1000 * 60 * 60 * 24) + 1);
    setdaysCount(daysCount);

    let finalExpiry = new Date(leastExpiry);
    let daysExpiry = Math.round(
      (finalExpiry - exd) / (1000 * 60 * 60 * 24) + 1
    );
    setDaysToExpiry(daysExpiry);

    updateTargetLine(g, daysExpiry);
  }, [leastExpiry, selectedData, data]);

  useEffect(() => {
    let j = ((sliderValue - lowerLimit) / (upperLimit - lowerLimit)) * 100;
    setleft(j);
  }, [sliderValue]);

  const handleDateClick = (event) => {
    let date = event.target.innerHTML;
    date = new Date(date);
    setDate(date);

    let exd = new Date(leastExpiry);
    let g = Math.round((exd - date) / (1000 * 60 * 60 * 24));
    setdaysleft(g);

    exd = new Date();
    let daysCount = Math.round((date - exd) / (1000 * 60 * 60 * 24) + 1);
    setdaysCount(daysCount);

    let finalExpiry = new Date(leastExpiry);
    let daysExpiry = Math.round(
      (finalExpiry - exd) / (1000 * 60 * 60 * 24) + 1
    );
    setDaysToExpiry(daysExpiry);
    // Computing target line

    updateTargetLine(g, daysExpiry);
    updateChart();
  };

  const handleSliderChange = (event) => {
    const value = parseInt(event.target.value);
    setSliderValue(value);
    if (duplicateVal) {
      let diff = value - Math.round(duplicateVal);
      diff /= duplicateVal;
      diff *= 100;
      diff = Math.round(diff * 100) / 100;
      setdiff(diff);
    }
  };

  const decrementLots = () => {
    if (lotsValue > 1) {
      setLotsValue(lotsValue - 1);
    }
  };

  const incrementLots = () => {
    setLotsValue(lotsValue + 1);
  };

  useEffect(() => {
    if (y_axis?.length > 0) {
      setAtmIndex(symbolData);
      updateChart();
    }
  }, [y_axis, calculatedIV, lotsValue, theme, targetPnLLine]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return "";
    }
    const daysOfWeek = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    const dayOfWeek = daysOfWeek[date.getDay()];
    const day = date.toLocaleDateString(undefined, { day: "2-digit" });
    const month = date.toLocaleDateString(undefined, { month: "short" });
    const year = date.toLocaleDateString(undefined, { year: "2-digit" });

    return `${dayOfWeek}, ${day} ${month} ${year}`;
  };

  return (
    <div className="strategy-graph flex flex-col justify-between py-3 w-full mt-2 ">
      {activeButton === "payoffchart" && (
        <>
          <div className="relative mt-[9px] flex items-center justify-between px-4">
            <OILegend />
            <div className="flex flex-row z-[400] px-[10px] py-[8px] items-center justify-center text-[10px] font-medium leading-[10px] text-p300 border-n200 border-[1px] rounded-md gap-1">
              <div className="">Current Price:</div>
              <div>{ltp}</div>
            </div>
            <div className="flex items-center gap-2">
              {/* Dropdown placeholder - you can add your dropdown component here */}
              <div className="w-4 h-4"></div>
            </div>
          </div>
          <div className="w-[100%] mt-[20px]">
            <HighchartsReact
              key={`${selectedSymbol?.scrip}-${selectedExpiry}`}
              highcharts={Highcharts}
              allowChartUpdate={true}
              options={chartOptions}
              ref={chartComponent}
            />
          </div>
        </>
      )}

      {activeButton === "p&ltable" && (
        <div className="main-graph-table table-container my-3 block">
          <div className="table-container">
            {window.innerWidth > 600 && (
              <table className="strategy-tablee">
                <thead>
                  <tr className="text-center tr-strategy-headerr w-auto">
                    <th scope="col" className="text-start ps-3 pe-5 py-2">
                      Instrument
                    </th>
                    <th scope="col">Lots</th>
                    <th scope="col">Entry price</th>
                    <th scope="col">Target price</th>
                    <th scope="col" className="pe-3">
                      Estimated P&L
                    </th>
                  </tr>
                </thead>
                <tbody className="tbody">
                  {selectedData &&
                    selectedData.map((obj, index) => (
                      <tr
                        className="strategy-table-roww text-center"
                        key={index}>
                        <td className="table-cell text-start ps-3 break-all overflow-hidden">
                          <div className="flex items-center">
                            {obj?.tradingSymbol}
                          </div>
                        </td>
                        <td className="table-cell">
                          {obj?.net_quantity
                            ? obj?.net_quantity / obj?.lotSize
                            : obj?.lotSize}
                        </td>
                        <td className="table-cell">
                          {obj?.price ? obj.price : obj?.average_prc}
                        </td>
                        <td className="table-cell">{obj?.targetPrice}</td>
                        <td
                          className={`table-cell pe-3 ${(obj?.isBuy ? -1 : 1) *
                            ((obj?.price ? obj.price : obj?.average_prc) -
                              obj?.targetPrice) >=
                            0
                            ? "text-success"
                            : "text-danger"
                            }`}>
                          ₹
                          {(
                            (obj?.isBuy ? -1 : 1) *
                            ((obj?.price ? obj.price : obj?.average_prc) -
                              obj?.targetPrice) *
                            obj?.lotSize *
                            (obj?.lot_multiplier ? obj.lot_multiplier : 1)
                          ).toFixed(2)}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            )}
            {window.innerWidth <= 430 && (
              <div className="text-center mt-[10px]">
                <table className="strategy-tablee">
                  <thead>
                    <tr className="text-center tr-strategy-headerr border border-[#EFEFEF]">
                      <tr className="flex justify-between items-center px-2 w-auto">
                        <th scope="col" className="py-2">
                          Instrument
                        </th>
                        <th scope="col">Lots</th>
                      </tr>
                      <tr className="flex justify-between items-center px-2 w-auto">
                        <th scope="col" className="pb-2">
                          Entry price
                        </th>
                        <th scope="col">Target price</th>
                        <th scope="col">Estimated P&L</th>
                      </tr>
                    </tr>
                  </thead>
                  <tbody className="tbody">
                    {selectedData &&
                      selectedData.map((obj, index) => (
                        <tr
                          className="strategy-table-roww text-center"
                          key={index}>
                          <tr className="flex items-center justify-between px-2 pb-2 w-auto">
                            <td className="table-cell">
                              <div className="flex items-center">
                                {obj?.tradingSymbol}
                              </div>
                            </td>
                            <td className="table-cell mt-1 flex items-center justify-center">
                              {obj?.lotSize}
                            </td>
                          </tr>
                          <tr className="flex justify-between items-center px-2 w-auto">
                            <td className="table-cell">
                              ₹{formatNumberWithCommasAndFontWeight(obj?.price)}
                            </td>
                            <td className="table-cell">
                              ₹
                              {formatNumberWithCommasAndFontWeight(
                                obj?.targetPrice
                              )}
                            </td>
                            <td
                              className={`table-cell ${(obj?.isBuy ? -1 : 1) *
                                ((obj?.price ? obj.price : obj?.average_prc) -
                                  obj?.targetPrice) >=
                                0
                                ? "text-success"
                                : "text-danger"
                                }`}>
                              ₹
                              {formatNumberWithCommasAndFontWeight(
                                (
                                  (obj?.isBuy ? -1 : 1) *
                                  ((obj?.price ? obj.price : obj?.average_prc) -
                                    obj?.targetPrice) *
                                  obj?.lotSize *
                                  (obj?.lot_multiplier ? obj.lot_multiplier : 1)
                                ).toFixed(2)
                              )}
                            </td>
                          </tr>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          <div className="flex justify-end items-center pe-3 mt-5">
            <span className="graph-text">Total estimated P&L</span>
            <img
              src={QuestionMarks}
              alt="Total estimated P&L"
              data-tooltip-id="total-est-pnl"
              className="tooltip-question-mark ms-2 me-3"
            />
            <span
              className={`estimated-total ${selectedData
                .map(
                  (obj) =>
                    (obj?.isBuy ? -1 : 1) *
                    ((obj?.price ? obj.price : obj?.average_prc) -
                      obj?.targetPrice) *
                    obj?.lotSize *
                    (obj?.lot_multiplier ? obj.lot_multiplier : 1)
                )
                .reduce((acc, value) => acc + value)
                .toFixed(2) >= 0
                ? "text-success"
                : "text-danger"
                }`}>
              ₹
              {formatNumberWithCommasAndFontWeight(
                selectedData
                  .map(
                    (obj) =>
                      (obj?.isBuy ? -1 : 1) *
                      ((obj?.price ? obj.price : obj?.average_prc) -
                        obj?.targetPrice) *
                      obj?.lotSize *
                      (obj?.lot_multiplier ? obj.lot_multiplier : 1)
                  )
                  .reduce((acc, value) => acc + value)
                  .toFixed(2)
              )}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default StrategyGraph;
